/**
 * Reviews API Contract Schemas
 *
 * Defines Zod schemas for reviews-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend (React forms).
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const ReviewSortByEnum = z.enum(['newest', 'oldest', 'highest', 'lowest']);
export type ReviewSortByValue = z.infer<typeof ReviewSortByEnum>;

/**
 * Admin moderation actions for reviews
 */
export const ReviewModerationActionEnum = z.enum(['approve', 'reject', 'flag']);
export type ReviewModerationActionValue = z.infer<typeof ReviewModerationActionEnum>;

// ============================================
// REQUEST SCHEMAS
// ============================================

/**
 * Schema for creating a new review
 */
export const CreateReviewSchema = z.object({
  bookingId: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().max(2000).optional(),
});
export type CreateReview = z.infer<typeof CreateReviewSchema>;

/**
 * Schema for updating an existing review
 */
export const UpdateReviewSchema = z.object({
  rating: z.number().int().min(1).max(5).optional(),
  comment: z.string().max(2000).optional(),
});
export type UpdateReview = z.infer<typeof UpdateReviewSchema>;

/**
 * Schema for vendor responding to a review
 */
export const VendorReviewResponseSchema = z.object({
  response: z
    .string()
    .min(1, 'Response is required')
    .max(500, 'Response must be 500 characters or less'),
});
export type VendorReviewResponseRequest = z.infer<typeof VendorReviewResponseSchema>;

/**
 * Schema for filtering/paginating reviews
 */
export const ReviewFilterSchema = z.object({
  page: z.number().int().min(1).default(1).optional(),
  limit: z.number().int().min(1).max(50).default(10).optional(),
  rating: z.number().int().min(1).max(5).optional(),
  sortBy: ReviewSortByEnum.default('newest').optional(),
});
export type ReviewFilter = z.infer<typeof ReviewFilterSchema>;

/**
 * Admin-only filter schema with additional moderation fields
 */
export const AdminReviewFilterSchema = ReviewFilterSchema.extend({
  isApproved: z.boolean().optional(),
  isReported: z.boolean().optional(),
  venueId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
});
export type AdminReviewFilter = z.infer<typeof AdminReviewFilterSchema>;

/**
 * Schema for admin moderation action
 */
export const ModerateReviewSchema = z.object({
  action: ReviewModerationActionEnum,
  reason: z.string().max(500).optional(),
});
export type ModerateReview = z.infer<typeof ModerateReviewSchema>;

// ============================================
// VENDOR REVIEW QUERY SCHEMAS
// ============================================

/**
 * Schema for vendor reviews list query
 * Used to filter and paginate reviews across all vendor's venues
 */
export const VendorReviewsQuerySchema = z.object({
  venueId: z.string().uuid().optional(),
  rating: z.coerce.number().int().min(1).max(5).optional(),
  hasResponse: z.coerce.boolean().optional(), // true = has response, false = no response
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  sortBy: z.enum(['createdAt', 'rating']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type VendorReviewsQuery = z.infer<typeof VendorReviewsQuerySchema>;

// ============================================
// RESPONSE SCHEMAS
// ============================================

/**
 * Customer info in review response (privacy-preserving format)
 */
const ReviewCustomerSchema = z.object({
  displayName: z.string(),
});
export type ReviewCustomer = z.infer<typeof ReviewCustomerSchema>;

/**
 * Single review response
 */
export const ReviewResponseSchema = z.object({
  id: z.string().uuid(),
  bookingId: z.string().uuid(),
  venueId: z.string().uuid(),
  customerId: z.string().uuid(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().nullable(),
  isApproved: z.boolean(),
  isReported: z.boolean(),
  images: z.array(z.string()),
  vendorResponse: z.string().max(500).nullable(),
  vendorResponseAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  customer: ReviewCustomerSchema,
});
export type ReviewResponse = z.infer<typeof ReviewResponseSchema>;

/**
 * Featured review for the public landing page testimonials section.
 * Contains privacy-preserving customer name (first name + last initial).
 */
export const FeaturedReviewSchema = z.object({
  id: z.string().uuid(),
  customerName: z.string(),
  rating: z.number().int().min(1).max(5),
  comment: z.string(),
  venueName: z.string(),
  date: z.string().datetime(),
});
export type FeaturedReview = z.infer<typeof FeaturedReviewSchema>;

export const FeaturedReviewsResponseSchema = z.object({
  data: z.array(FeaturedReviewSchema),
});
export type FeaturedReviewsResponse = z.infer<typeof FeaturedReviewsResponseSchema>;

/**
 * Paginated reviews response
 * @deprecated Use createPaginatedSchema(ReviewResponseSchema) instead
 * This schema doesn't match the actual API envelope structure which includes success, data, and meta fields.
 * The backend ResponseTransformInterceptor wraps responses in { success: true, data: T[], meta: { pagination } }
 */
export const PaginatedReviewsResponseSchema = z.object({
  data: z.array(ReviewResponseSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type PaginatedReviewsResponse = z.infer<typeof PaginatedReviewsResponseSchema>;
